import React, { useState, useEffect } from 'react';
import {
    Spin, Button, Card, Tag, message, Tooltip, Input, Space, Table
} from 'antd';
import {
    AppstoreOutlined,
    StopOutlined,
    CheckOutlined,
    SyncOutlined,
} from '@ant-design/icons';
import 'antd/dist/antd.css';
import { useHistory } from "react-router-dom";
import Fuse from 'fuse.js';
import _ from 'lodash';
import moment from 'moment';
import * as Service from '../core/Service';
import NavAndSideFrame from '../components/NavAndSideFrame';
import { useTranslation } from 'react-i18next';
import { displayIsActive } from '../components/table/DisplayMethod';

const title = `司機列表`;
const selectedKey = `driver_list`;
const tableIdName = `driver_id`;
const filterableField = [
    'driver_name',
    'email',
    'mobile',
    'call_sign'
];

const DriverList = (props) => {
    const {t} = useTranslation();

    const history = useHistory();
    const [dataList, setDataList] = useState([]);
    const [unChangeData, setUnChangeData] = useState([]);
    const [isChangingIsActive, setChangingIsActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isRecoverIsActive, setRecoverIsActive] = useState(false);

    useEffect(() => {
        getAllData();
    }, []);

    const getAllData = async () => {
        let endpoint = `/api/driver/list`;
        let list = [];
        setLoading(true);
        try {
            let data = await Service.call(`GET`, endpoint);
            list = _.orderBy(data, [`ctime`], [`desc`]);
            if (data.errorMessage) list = [];
        } catch (error) {
            console.log(error);
        } finally {
            setDataList(list);
            setUnChangeData(list);
            setLoading(false);
        }
    }

    const setTableHeader = () => {
        let columns = [
            {
                title: '操作',
                dataIndex: tableIdName,
                render: (value, record) => {
                    return (
                        <span>
                            <Tooltip title={t('common.view_details')}>
                                <Button
                                    shape="circle"
                                    style={{ marginRight: 8, color: '#000000' }}
                                    icon={<AppstoreOutlined />}
                                    onClick={() => {
                                        displayDriverDetails(record.driver_id);
                                    }}
                                />
                            </Tooltip>
                            <Tooltip title={record.is_active ? t('driver.disable_driver') : t('driver.enable_driver')}>
                                <Button
                                    shape="circle"
                                    style={{ marginRight: record.is_deleted ? 8 : 0, color: record.is_active ? 'red' : 'green' }}
                                    icon={record.is_active ? (<StopOutlined />) : (<CheckOutlined />)}
                                    onClick={async () => {
                                        if (isChangingIsActive) return; // avoid multiple attempts

                                        setChangingIsActive(true);
                                        let { driver_id, is_active } = record;
                                        is_active = !is_active;

                                        let result = await Service.call('put', `/api/driver`, { driver_id, is_active });
                                        setChangingIsActive(false);

                                        if (_.isEmpty(result) || result.status < 1) {
                                            return message.error(is_active ? t('driver.enable_failed') : t('driver.disable_failed'));
                                        }
                                        await getAllData();
                                        return message.success(is_active ? t('driver.enabled_driver') : t('driver.disabled_driver'));
                                    }}
                                />
                            </Tooltip>
                            {record.is_deleted
                                ? (
                                    <Tooltip title={t('driver.recover_deleted_driver')}>
                                        <Button
                                            shape="circle"
                                            style={{ color: 'green' }}
                                            icon={(<SyncOutlined />)}
                                            onClick={async () => {
                                                if (isRecoverIsActive) return; // avoid multiple attempts

                                                setRecoverIsActive(true);

                                                const { driver_id } = record;
                                                const result = await Service.call('put', `/api/driver`, { driver_id, is_deleted: 0 });
                                                setRecoverIsActive(false);

                                                if (_.isEmpty(result) || result.status < 1) {
                                                    return message.error(t('driver.recover_driver_failed'));
                                                }
                                                await getAllData();
                                                return message.success(t('driver.recover_driver_success'));
                                            }}
                                        />
                                    </Tooltip>
                                ) : null
                            }
                        </span>
                    )
                }
            },
            {
              title: '呼號',
              dataIndex: 'call_sign',
              render: (value) => {
                if (_.isNil(value)) return "-";
                  const child = _.map(value.split(','), (cs) => {
                    return <Tag>{cs}</Tag>
                  });
                  return <div style={{maxWidth: "10vw", display: "flex", flexWrap: "wrap"}}>{child}</div>
              },
              ellipsis: true,
            },
            { title: '司機編號', dataIndex: tableIdName },
            { title: '現有儲值額', dataIndex: 'stored_value' },
            {
                title: '生效狀態',
                dataIndex: 'is_active',
                render: (value) => {
                    return displayIsActive(value, t);
                },
                ellipsis: true
            },
            {
                title: `電話號碼`,
                dataIndex: `mobile`
            },
            { title: `電郵`, dataIndex: `email` },
            { title: `司機姓名`, dataIndex: `driver_name` },
            {
                title: '創建時間',
                dataIndex: 'ctime',
                render: (value) => {
                    return displayMoment(value);
                },
                ellipsis: true,
            },
            {
                title: '更新時間',
                dataIndex: 'utime',
                render: (value) => {
                    return displayMoment(value);
                },
                ellipsis: true,
            },
            {
              title: '帳號已刪除？',
              dataIndex: 'is_deleted',
              render: (value) => {
                  return displayBoolean(value);
              },
          },
        ];
        return columns;
    }

    const displayMoment = (unixTime, outputFormat = 'YYYY/MM/DD HH:mm') => {
        if (unixTime !== 0) {
            let displayTime = moment.unix(unixTime).format(outputFormat);
            return displayTime;
        }
        return '-';
    }
    const displayBoolean = (value) => {
      if (value === 1 || value === true) return <Tag color="red">是</Tag>
      return <Tag>否</Tag>
    }

    const getDataBySearchKeyword = (keywords) => {
        let dataList = [];
        try {
            const options = {
                includeScore: true,
                findAllMatches: true,
                threshold: 0.15,
                keys: filterableField
            }
            const fuse = new Fuse(unChangeData, options)
            const result = fuse.search(keywords);
            console.log("RESULT", result);
            dataList = _.map(result, 'item');
            dataList = _.orderBy(dataList, [`ctime`], [`desc`]);
        } catch (error) {
            console.error('error >>> ', error);
        } finally {
            setDataList(dataList);
        }
    }

    let timeout;
    const debounce = (func, delay) => {
        clearTimeout(timeout);
        timeout = setTimeout(func, delay);
    };

    const onChange = (keyword) => {
        debounce(() => {
            getDataBySearchKeyword(keyword);
            if (_.isEmpty(keyword)) setDataList(unChangeData);
        }, 400);
    };

    const displayDriverDetails = (driver_id) => {
        let redirectedLink = `/driver_info?driver_id=${driver_id}`;
        history.push(redirectedLink);
    }

    return (
        <NavAndSideFrame {...props} title={title} selectedKey={selectedKey}>
            <Card>
                <Space direction="horizontal">
                    <>以文字搜尋:</>
                    <Input
                        placeholder="請輸入文字"
                        onChange={(values) => {
                            onChange(values.target.value);
                        }}
                    />
                </Space>
            </Card>
            <Card>
                <Spin spinning={loading}>
                    <Table
                        pagination={{
                            defaultPageSize: 25,
                            showSizeChanger: true,
                            pageSizeOptions: [25, 50, 100, 200, 500]
                        }}

                        scroll={{ x: `max-content` }}
                        dataSource={dataList}
                        columns={setTableHeader()}
                        rowKey={tableIdName}
                    />
                </Spin>
            </Card>
        </NavAndSideFrame>
    );
}

export default DriverList;
